/** Interval-capable Binance kline adapter. */

import { Interval } from "../../../../core/interval.ts";
import { createPollingSignalSource, type SignalAdapter } from "../../fetcher.ts";
import { pickResolution } from "../../resolution.ts";
import { logPriceSamples, type PricePoint } from "../price.ts";

const BINANCE_KLINES = "https://api.binance.com/api/v3/klines";
const LIMIT = 1_000;

const BINANCE_LADDER: readonly { readonly periodMs: number; readonly interval: string }[] = [
  { periodMs: 60_000, interval: "1m" },
  { periodMs: 3 * 60_000, interval: "3m" },
  { periodMs: 5 * 60_000, interval: "5m" },
  { periodMs: 15 * 60_000, interval: "15m" },
  { periodMs: 30 * 60_000, interval: "30m" },
  { periodMs: 60 * 60_000, interval: "1h" },
  { periodMs: 2 * 60 * 60_000, interval: "2h" },
  { periodMs: 4 * 60 * 60_000, interval: "4h" },
  { periodMs: 6 * 60 * 60_000, interval: "6h" },
  { periodMs: 8 * 60 * 60_000, interval: "8h" },
  { periodMs: 12 * 60 * 60_000, interval: "12h" },
  { periodMs: 24 * 60 * 60_000, interval: "1d" },
  { periodMs: 3 * 24 * 60 * 60_000, interval: "3d" },
  { periodMs: 7 * 24 * 60 * 60_000, interval: "1w" },
];

const PERIODS = BINANCE_LADDER.map(entry => entry.periodMs);

export interface BinanceAdapterOptions {
  readonly symbol: string;
  readonly timeoutMs?: number;
}

export function createBinanceAdapter(opts: BinanceAdapterOptions): SignalAdapter {
  const symbol = opts.symbol.trim().toUpperCase();
  if (!/^[A-Z0-9]{4,30}$/.test(symbol)) {
    throw new Error(`Invalid Binance symbol: ${opts.symbol}`);
  }

  return createPollingSignalSource({
    minFetchPoints: 256,
    retryDelayMs(error, attempt) {
      const message = error instanceof Error ? error.message : String(error);
      if (message.includes("429") || message.includes("418")) return 60_000;
      return Math.min(30_000, 1_000 * 2 ** (attempt - 1));
    },

    resolve({ maxDeltaTMs }) {
      return pickResolution(PERIODS, maxDeltaTMs);
    },

    async fetchInterval({ range, resolutionMs: periodMs }, signal) {
      const entry = BINANCE_LADDER.find(candidate => candidate.periodMs === periodMs)!;
      const params = new URLSearchParams({
        symbol,
        interval: entry.interval,
        startTime: Math.floor(range.start - periodMs).toString(),
        endTime: Math.floor(range.end).toString(),
        limit: LIMIT.toString(),
      });
      const response = await fetch(`${BINANCE_KLINES}?${params}`, {
        signal: AbortSignal.any([signal, AbortSignal.timeout(opts.timeoutMs ?? 15_000)]),
      });
      if (!response.ok) {
        throw new Error(`Binance klines failed: ${response.status} ${response.statusText}`);
      }
      const raw: unknown = await response.json();
      if (!Array.isArray(raw)) throw new Error("Binance klines returned a non-array payload");

      const points: PricePoint[] = [];
      for (const row of raw) {
        if (!Array.isArray(row)) continue;
        const t = Number(row[0]);
        const price = Number(row[1]);
        if (Number.isFinite(t) && Number.isFinite(price) && price > 0) points.push({ t, price });
      }

      let searchedInterval = range;
      const last = points[points.length - 1];
      if (raw.length >= LIMIT && last !== undefined) {
        const searchedMax = Math.min(range.end, last.t + periodMs);
        if (range.start < searchedMax) searchedInterval = Interval.create(range.start, searchedMax);
      }
      return { samples: logPriceSamples(points), searchedInterval };
    },
  });
}
